import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import type { PointerEvent } from 'react';

import type { CameraState, VideoBroadcastWidgetState } from '../store/VideoBroadcastWidgetState';

import { CameraEntity } from './CameraEntity';
import styles from './VideoBroadcastWidget.module.scss';

type Props = {
  widgetState: VideoBroadcastWidgetState;
  columns: number;
  rows: number;
  cameraHeight: number;
  cameraWidth: number;
  onVisibleCamerasChange: (cameras: CameraState[]) => void;
  selectedCameras: CameraState[];
};

export const Carousel = observer(function Carousel({
  widgetState,
  cameraHeight,
  cameraWidth,
  columns,
  onVisibleCamerasChange,
  rows,
  selectedCameras,
}: Props) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragPointerIdRef = useRef<number | null>(null);
  const dragStartXRef = useRef(0);
  const dragStartSlideRef = useRef(0);
  const ignoreCurrentDragRef = useRef(false);
  const carouselViewportRef = useRef<HTMLDivElement>(null);
  const camsQuantity = rows * columns;
  const selectedCameraIdsKey = selectedCameras.map((camera) => camera.id).join(':');
  const slidesQuantity = Math.ceil(selectedCameras.length / camsQuantity);
  const currentVisibleSlide = clampSlideIndex(currentSlide, slidesQuantity);
  const slideIndexes = Array.from({ length: slidesQuantity }, (_, index) => index);

  useEffect(() => {
    if (currentSlide !== currentVisibleSlide) {
      setCurrentSlide(currentVisibleSlide);
    }
  }, [currentSlide, currentVisibleSlide]);

  useEffect(() => {
    const start = currentVisibleSlide * camsQuantity;
    onVisibleCamerasChange(selectedCameras.slice(start, start + camsQuantity));
  }, [camsQuantity, currentVisibleSlide, onVisibleCamerasChange, selectedCameraIdsKey, selectedCameras]);

  const finishDrag = (offset: number) => {
    const carouselViewport = carouselViewportRef.current;
    const slideWidth = carouselViewport?.clientWidth ?? 0;
    const shouldChangeSlide = slideWidth > 0 && Math.abs(offset) > slideWidth / 4;
    const direction = offset > 0 ? -1 : 1;
    const nextSlide = shouldChangeSlide
      ? clampSlideIndex(dragStartSlideRef.current + direction, slidesQuantity)
      : dragStartSlideRef.current;

    setCurrentSlide(nextSlide);
    setDragOffset(0);
    setIsDragging(false);
    dragPointerIdRef.current = null;
    ignoreCurrentDragRef.current = false;
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    ignoreCurrentDragRef.current =
      event.target instanceof Element && event.target.closest('[data-carousel-drag-disabled]') != null;

    if (ignoreCurrentDragRef.current) {
      return;
    }

    dragPointerIdRef.current = event.pointerId;
    dragStartXRef.current = event.clientX;
    dragStartSlideRef.current = currentVisibleSlide;
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDragging || ignoreCurrentDragRef.current || dragPointerIdRef.current !== event.pointerId) {
      return;
    }

    setDragOffset(event.clientX - dragStartXRef.current);
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDragging || dragPointerIdRef.current !== event.pointerId) {
      return;
    }

    event.currentTarget.releasePointerCapture(event.pointerId);
    finishDrag(event.clientX - dragStartXRef.current);
  };

  const handlePointerCancel = () => {
    setDragOffset(0);
    setIsDragging(false);
    dragPointerIdRef.current = null;
    ignoreCurrentDragRef.current = false;
  };

  const prevSlide = () => {
    if (currentVisibleSlide === 0) return;

    setCurrentSlide((prevSlide) => clampSlideIndex(prevSlide - 1, slidesQuantity));
  };

  const nextSlide = () => {
    if (slidesQuantity - 1 === currentVisibleSlide) return;

    setCurrentSlide((prevSlide) => clampSlideIndex(prevSlide + 1, slidesQuantity));
  };

  return (
    <>
      <div
        className={styles.carouselViewport}
        ref={carouselViewportRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <div
          className={clsx(
            styles.carouselTrack,
            isDragging ? styles.carouselTrackDragging : styles.carouselTrackSettled,
          )}
          style={{ transform: `translateX(calc(-${currentVisibleSlide * 100}% + ${dragOffset}px))` }}
        >
          {selectedCameras.map((camera, cameraIndex) => {
            const slideIndex = Math.floor(cameraIndex / camsQuantity);
            const cameraIndexOnSlide = cameraIndex % camsQuantity;
            const columnIndex = cameraIndexOnSlide % columns;
            const rowIndex = Math.floor(cameraIndexOnSlide / columns);

            return (
              <div
                className={styles.cameraCell}
                key={camera.id}
                style={{
                  height: cameraHeight,
                  left: `calc(${slideIndex * 100}% + 12px + ${columnIndex * cameraWidth}px)`,
                  top: rowIndex * cameraHeight,
                  width: cameraWidth,
                }}
              >
                <CameraEntity active={slideIndex === currentVisibleSlide} camera={camera} widgetState={widgetState} />
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.carouselControllerContainer}>
        <button
          aria-label="Previous video page"
          className={clsx(styles.slideArrow, styles.slideArrowLeft)}
          disabled={currentVisibleSlide === 0}
          onClick={prevSlide}
          type="button"
        />
        <div className={styles.carouselDots}>
          {slideIndexes.map((i) => (
            <button
              aria-label={`Open video page ${i + 1}`}
              className={clsx(styles.carouselDot, currentVisibleSlide === i && styles.carouselDotActive)}
              onClick={() => setCurrentSlide(i)}
              key={i}
              type="button"
            />
          ))}
        </div>

        <button
          aria-label="Next video page"
          className={clsx(styles.slideArrow, styles.slideArrowRight)}
          disabled={currentVisibleSlide + 1 >= slidesQuantity}
          onClick={nextSlide}
          type="button"
        />
      </div>
    </>
  );
});

function clampSlideIndex(index: number, slidesQuantity: number) {
  return Math.min(Math.max(index, 0), Math.max(slidesQuantity - 1, 0));
}
